// ---------------------------------------------------------------------------
// Toaster for `@/components/ui/use-toast` (sonner)
// ---------------------------------------------------------------------------
// AL-1861: `useToast()` from `@/components/ui/use-toast` dispatches through
// sonner, but only the Radix <Toaster/> (`@/components/ui/toaster`, fed by
// `@/hooks/use-toast`) was ever mounted — so every one of those toasts (profile
// saves, avatar, password, sessions, segments…) went nowhere. This is the
// sonner side, mounted next to the Radix one in App.
//
// The theme comes from the app's ThemeContext (there is no next-themes
// provider) and the look from the v11 tokens, which follow light/dark on
// their own.
// ---------------------------------------------------------------------------

import { Toaster as Sonner, toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { resolvedTheme } = useTheme();

  return (
    <Sonner
      theme={resolvedTheme}
      position="bottom-right"
      closeButton
      className="toaster group"
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            'flex w-full items-start gap-3 rounded-xl border border-v11-border bg-v11-surface p-4 text-sm text-v11-text-primary shadow-lg sm:w-[356px]',
          title: 'font-semibold',
          description: 'mt-1 text-v11-text-muted',
          icon: 'mt-0.5',
          error: '**:data-icon:text-v11-chart-negative **:data-title:text-v11-chart-negative',
          success: '**:data-icon:text-v11-chart-positive',
          closeButton:
            'border-v11-border bg-v11-surface text-v11-text-muted hover:text-v11-text-primary',
          actionButton: 'rounded-full bg-v11-cta-bg px-3 py-1 text-v11-cta-text',
          cancelButton: 'rounded-full px-3 py-1 text-v11-text-muted',
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
